import { useState, useMemo, useCallback } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
const fmtQty = (n: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n);

type CellKey = string; // "unitId::sovLineId"
const cellKey = (unitId: string, sovLineId: string): CellKey => `${unitId}::${sovLineId}`;
const parseKey = (key: CellKey) => {
  const [unitId, sovLineId] = key.split("::");
  return { unitId, sovLineId };
};

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500] as const;

interface Props {
  units: any[];
  sovLines: any[];
  scopeItems: any[];
  existingWoLines: any[];
  contractorId: string;
  onSelectionChange: (selected: CellKey[]) => void;
  selectedCells: Set<CellKey>;
  onToggleCell: (key: CellKey) => void;
  onUnassign?: (wolId: string) => Promise<void>;
}

export const WOAssignmentGrid = ({
  units, sovLines, scopeItems, existingWoLines, contractorId,
  onSelectionChange, selectedCells, onToggleCell, onUnassign,
}: Props) => {
  const [search, setSearch] = useState("");
  const [sovFilter, setSovFilter] = useState<string>("all");
  const [pageSize, setPageSize] = useState<number>(50);
  const [page, setPage] = useState(0);
  const [unassignTarget, setUnassignTarget] = useState<any>(null);
  const [unassignBusy, setUnassignBusy] = useState(false);

  // Build scope lookup: unitId -> sovLineId -> scopeItem
  const scopeMap = useMemo(() => {
    const m = new Map<string, Map<string, any>>();
    (scopeItems ?? []).forEach((si: any) => {
      if (!si.has_scope) return;
      if (!m.has(si.unit_id)) m.set(si.unit_id, new Map());
      m.get(si.unit_id)!.set(si.sov_line_id, si);
    });
    return m;
  }, [scopeItems]);

  // Build existing assignment lookup — exclude voided/closed WO lines AND voided/closed parent WOs
  const assignedMap = useMemo(() => {
    const m = new Map<CellKey, any>();
    (existingWoLines ?? []).forEach((wol: any) => {
      const woStatus = wol.work_orders?.status;
      // Skip if parent WO is voided or closed
      if (woStatus === "voided" || woStatus === "cancelled") return;
      // Skip if line itself is voided, released, or paid
      if (wol.unit_id && wol.status !== "voided" && wol.status !== "released" && wol.status !== "paid") {
        m.set(cellKey(wol.unit_id, wol.sov_line_id), wol);
      }
    });
    return m;
  }, [existingWoLines]);

  // Filter units by search
  const filteredUnits = useMemo(() => {
    let u = units ?? [];
    if (search) u = u.filter((unit: any) => unit.unit_number.toLowerCase().includes(search.toLowerCase()));
    return u;
  }, [units, search]);

  // Reset page when search changes
  const totalPages = Math.max(1, Math.ceil(filteredUnits.length / pageSize));
  const safeP = Math.min(page, totalPages - 1);
  const pagedUnits = filteredUnits.slice(safeP * pageSize, safeP * pageSize + pageSize);

  // Only show SOV lines that have scope items for at least one unit
  const activeSovLines = useMemo(() => {
    return (sovLines ?? []).filter((sl: any) =>
      (units ?? []).some((u: any) => scopeMap.get(u.id)?.has(sl.id))
    );
  }, [sovLines, units, scopeMap]);

  const getCellState = (unitId: string, sovLineId: string) => {
    const si = scopeMap.get(unitId)?.get(sovLineId);
    if (!si) return { type: "no_scope" as const };

    const key = cellKey(unitId, sovLineId);
    const existing = assignedMap.get(key);

    if (existing) {
      if (existing.status === "billed") return { type: "billed" as const, wol: existing };
      return { type: "assigned" as const, wol: existing };
    }

    return { type: "available" as const, scopeItem: si };
  };

  const selectAll = () => {
    const keys: CellKey[] = [];
    filteredUnits.forEach((unit: any) => {
      activeSovLines.forEach((sl: any) => {
        if (sovFilter !== "all" && sl.id !== sovFilter) return;
        const state = getCellState(unit.id, sl.id);
        if (state.type === "available") keys.push(cellKey(unit.id, sl.id));
      });
    });
    onSelectionChange(keys);
  };

  const clearAll = () => onSelectionChange([]);

  const selectBySovLine = (sovLineId: string) => {
    const keys = new Set(selectedCells);
    filteredUnits.forEach((unit: any) => {
      const state = getCellState(unit.id, sovLineId);
      if (state.type === "available") keys.add(cellKey(unit.id, sovLineId));
    });
    onSelectionChange(Array.from(keys));
  };

  const selectByUnit = (unitId: string) => {
    const keys = new Set(selectedCells);
    activeSovLines.forEach((sl: any) => {
      const state = getCellState(unitId, sl.id);
      if (state.type === "available") keys.add(cellKey(unitId, sl.id));
    });
    onSelectionChange(Array.from(keys));
  };

  // Summary
  const summary = useMemo(() => {
    const groups: Record<string, { sovLine: any; units: string[]; totalQty: number; totalValue: number }> = {};
    selectedCells.forEach(key => {
      const { unitId, sovLineId } = parseKey(key);
      const sl = activeSovLines.find((s: any) => s.id === sovLineId);
      const si = scopeMap.get(unitId)?.get(sovLineId);
      const unit = (units ?? []).find((u: any) => u.id === unitId);
      if (!sl || !si || !unit) return;
      if (!groups[sovLineId]) groups[sovLineId] = { sovLine: sl, units: [], totalQty: 0, totalValue: 0 };
      groups[sovLineId].units.push(unit.unit_number);
      groups[sovLineId].totalQty += Number(si.quantity);
      groups[sovLineId].totalValue += Number(si.quantity) * Number(sl.labor_unit_price ?? sl.unit_price ?? 0);
    });
    return Object.values(groups);
  }, [selectedCells, activeSovLines, scopeMap, units]);

  const grandTotal = summary.reduce((s, g) => s + g.totalValue, 0);

  return (
    <TooltipProvider>
      <div className="space-y-3">
        {/* Controls */}
        <div className="flex flex-wrap gap-2 items-center">
          <Input
            placeholder="Search units..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            className="w-40 h-8"
          />
          <Button variant="outline" size="sm" onClick={selectAll}>Select All</Button>
          <Button variant="outline" size="sm" onClick={clearAll}>Clear All</Button>
          <Select value={sovFilter} onValueChange={v => { setSovFilter(v); if (v !== "all") selectBySovLine(v); }}>
            <SelectTrigger className="w-48 h-8">
              <SelectValue placeholder="Select by SOV Line" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All SOV Lines</SelectItem>
              {activeSovLines.map((sl: any) => (
                <SelectItem key={sl.id} value={sl.id}>
                  {sl.item_code ?? `#${sl.line_number}`} {sl.description}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="ml-auto flex items-center gap-1.5">
            <span className="text-xs text-muted-foreground">Show</span>
            <Select value={String(pageSize)} onValueChange={v => { setPageSize(Number(v)); setPage(0); }}>
              <SelectTrigger className="w-20 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map(n => (
                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">units</span>
          </div>
        </div>

        {/* Grid with native scroll */}
        <div className="overflow-auto border rounded-md" style={{ maxHeight: "60vh" }}>
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 z-10 bg-background">
              <tr>
                <th className="border p-1.5 text-left text-xs font-medium text-muted-foreground sticky left-0 z-20 bg-background min-w-[80px]">Unit</th>
                {activeSovLines.map((sl: any) => (
                  <th
                    key={sl.id}
                    className="border p-1.5 text-center text-xs font-medium min-w-[110px] cursor-pointer hover:bg-accent/50 transition-colors"
                    onClick={() => selectBySovLine(sl.id)}
                  >
                    <div className="font-mono text-[10px] text-muted-foreground">{sl.item_code ?? `#${sl.line_number}`}</div>
                    <div className="truncate max-w-[100px]">{sl.description}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagedUnits.map((unit: any) => (
                <tr key={unit.id}>
                  <td
                    className="border p-1.5 font-mono text-xs font-medium sticky left-0 z-10 bg-background cursor-pointer hover:bg-accent/50"
                    onClick={() => selectByUnit(unit.id)}
                  >
                    {unit.unit_number}
                  </td>
                  {activeSovLines.map((sl: any) => {
                    const state = getCellState(unit.id, sl.id);
                    const key = cellKey(unit.id, sl.id);
                    const isSelected = selectedCells.has(key);

                    if (state.type === "no_scope") {
                      return <td key={sl.id} className="border p-1 text-center text-muted-foreground/30">—</td>;
                    }

                    if (state.type === "billed") {
                      return (
                        <td key={sl.id} className="border p-1 text-center">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-muted-foreground cursor-default">●</span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Billed · {state.wol.work_orders?.number}</p>
                            </TooltipContent>
                          </Tooltip>
                        </td>
                      );
                    }

                    if (state.type === "assigned") {
                      const woName = state.wol.work_orders?.vendors?.name ?? "";
                      const initials = woName.split(" ").map((w: string) => w[0]).join("").slice(0, 2).toUpperCase();
                      const woNumber = state.wol.work_orders?.number ?? "";
                      const woStatus = state.wol.work_orders?.status;
                      const canUnassign = onUnassign && woStatus !== "closed" && state.wol.status === "pending";
                      return (
                        <td
                          key={sl.id}
                          className={cn("border p-1 text-center", canUnassign && "cursor-pointer hover:bg-destructive/10 transition-colors")}
                          onClick={canUnassign ? () => setUnassignTarget(state.wol) : undefined}
                        >
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="secondary" className={cn("text-[9px] px-1", canUnassign ? "cursor-pointer" : "cursor-default")}>{initials || "?"}</Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Assigned to {woName} · {woNumber}</p>
                              {canUnassign && <p className="text-xs text-muted-foreground">Click to unassign</p>}
                            </TooltipContent>
                          </Tooltip>
                        </td>
                      );
                    }

                    return (
                      <td
                        key={sl.id}
                        className={cn(
                          "border p-1 text-center cursor-pointer transition-colors",
                          isSelected ? "bg-primary/10" : "hover:bg-accent/30"
                        )}
                        onClick={() => onToggleCell(key)}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => onToggleCell(key)}
                          onClick={e => e.stopPropagation()}
                          className="mx-auto"
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            Showing {safeP * pageSize + 1}–{Math.min((safeP + 1) * pageSize, filteredUnits.length)} of {filteredUnits.length} units
          </span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="h-7 px-2" disabled={safeP === 0} onClick={() => setPage(safeP - 1)}>Prev</Button>
            <Button variant="outline" size="sm" className="h-7 px-2" disabled={safeP >= totalPages - 1} onClick={() => setPage(safeP + 1)}>Next</Button>
          </div>
        </div>

        {/* Summary */}
        {summary.length > 0 && (
          <div className="border rounded-md p-3 space-y-1 bg-muted/30 text-sm">
            <div className="text-xs font-medium text-muted-foreground mb-1">Selected: {selectedCells.size} lines</div>
            {summary.map(g => (
              <div key={g.sovLine.id} className="flex justify-between">
                <span>
                  <span className="font-mono text-xs text-muted-foreground mr-1">{g.sovLine.item_code ?? `#${g.sovLine.line_number}`}</span>
                  {g.sovLine.description} · Units {g.units.join(", ")}
                </span>
                <span className="font-medium">{fmt(g.totalValue)}</span>
              </div>
            ))}
            <div className="border-t pt-1 flex justify-between font-medium">
              <span>Total assigned to this WO:</span>
              <span>{fmt(grandTotal)}</span>
            </div>
          </div>
        )}
        {/* Unassign confirmation dialog */}
        {unassignTarget && onUnassign && (
          <Dialog open={!!unassignTarget} onOpenChange={(o) => { if (!o) setUnassignTarget(null); }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Unassign Line</DialogTitle>
                <DialogDescription>
                  Remove <strong>{unassignTarget.unit_schedule?.unit_number ?? "this unit"}</strong> — {unassignTarget.description} from <strong>{unassignTarget.work_orders?.vendors?.name ?? "this WO"}</strong> ({unassignTarget.work_orders?.number})?
                  The unit will be returned to the available pool.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setUnassignTarget(null)} disabled={unassignBusy}>Cancel</Button>
                <Button variant="destructive" disabled={unassignBusy} onClick={async () => {
                  setUnassignBusy(true);
                  try {
                    await onUnassign(unassignTarget.id);
                    toast.success(`${unassignTarget.unit_schedule?.unit_number ?? "Unit"} unassigned`);
                    setUnassignTarget(null);
                  } catch (e: any) {
                    toast.error(e.message ?? "Failed to unassign");
                  } finally {
                    setUnassignBusy(false);
                  }
                }}>
                  {unassignBusy ? "Removing…" : "Unassign"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </TooltipProvider>
  );
};

export { cellKey, parseKey };
export type { CellKey };
